import { getAnalyticsInstance } from './firebase';

getAnalyticsInstance().then(analytics => {
  if (!analytics) return;
  // log events if you want
});

import './styles/index.scss';
import { initDropdowns } from 'g3d-ui';

/* =======================================================================================
   Website Navigation + Page Lifecycle Manager
   =======================================================================================
   What this does
   ---------------------------------------------------------------------------------------
   • Intercepts same-origin links (e.g., <a href="/contact">) and soft-navigates via fetch.
     – Same-page hash links (e.g., <a href="#section">) are NOT intercepted; the browser
       performs native smooth scrolling (respect your CSS scroll-behavior/offsets).
     – External links, modified clicks (Cmd/Ctrl/Shift/Alt), target != _self, downloads,
       or rel="external" are NOT intercepted.

   • Swaps the #content container with a fade transition.

   • Updates document.title, pushes/replaces History entries, and saves/restores scroll.

   • Runs page-specific init/cleanup hooks if defined (lazy-loaded per page).

   • Re-initializes global enhancements (dropdowns, a11y focus to #content).

   Architecture notes
   ---------------------------------------------------------------------------------------
   • MPA (multi-page app) for SEO: each page is a full HTML document. The router
     enhances navigation but the site still works without JS.

   • Progressive enhancement fallback: on any fetch/parse error we hard-navigate.

   • Accessibility: after swapping #content we move focus there so SR users are informed.

   • Safety & robustness:
     – AbortController cancels in-flight fetches on rapid clicks.
     – DOMParser parses off-document, so inline scripts in fetched HTML won’t execute.
     – Page init/cleanup wrapped in try/catch.
     – Same-page hash links bypass the router to avoid fade/scroll conflicts.

   CSS expectations
   ---------------------------------------------------------------------------------------
   • Your fixed header offset should be handled in CSS, e.g.:
       :root { --g3d-nav-height: 64px; scroll-behavior: smooth; }
       [id] { scroll-margin-top: var(--g3d-nav-height); }
     (Avoid transitioning header height during scroll.)
   ======================================================================================= */

/* Types ------------------------------------------------------------------------------- */
type PageModule = {
  /** Called after content is swapped into DOM */
  init?: () => Promise<void> | void;
  /** Called before navigating away from the page */
  cleanup?: () => void;
};

type PageRegistry = Record<string, () => Promise<PageModule>>;

/** Optional lazy page modules (add as you create them) */
const pages: PageRegistry = {
  // contact: () => import('../contact/'),
};

/* Config ------------------------------------------------------------------------------ */
const CONTENT_SELECTOR = '#content';
/** Duration (ms) fallback for fade; we also read actual CSS transition timing */
const TRANSITION_MS = 250;

/** Use manual scroll restoration; we manage it around swaps/history */
history.scrollRestoration = 'manual';

/* State --------------------------------------------------------------------------------*/
let currentCleanup: (() => void) | undefined;
let inFlight: AbortController | null = null;
/** For popstate: track last path+search to detect hash-only history changes */
let lastPathSearch = '';

/* Utilities ----------------------------------------------------------------------------*/
function isModifiedClick(e: MouseEvent): boolean {
  return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
}

function sameOrigin(url: URL): boolean {
  return url.origin === window.location.origin;
}

/** Merge scrollX/scrollY into history.state so we can restore it later */
function saveScrollInHistoryState(): void {
  const base: Record<string, unknown> =
    typeof history.state === 'object' && history.state !== null
      ? { ...(history.state as Record<string, unknown>) }
      : {};
  const next = { ...base, scrollX: window.scrollX, scrollY: window.scrollY };
  history.replaceState(next, '', window.location.href);
}

/** Restore scroll from a previously saved history.state */
function restoreScrollFromState(state: unknown): void {
  const s =
    typeof state === 'object' && state !== null
      ? (state as { scrollX?: unknown; scrollY?: unknown })
      : {};
  const x = typeof s.scrollX === 'number' ? s.scrollX : 0;
  const y = typeof s.scrollY === 'number' ? s.scrollY : 0;
  window.scrollTo(x, y);
}

/** Derive page id (for lazy init) from data attributes or pathname */
function getNextPageId(doc: Document): string {
  const fromContent =
    doc.querySelector<HTMLElement>(CONTENT_SELECTOR)?.dataset.page;
  if (fromContent) return fromContent;

  const fromBody = (doc.body as HTMLElement | null)?.dataset?.page;
  if (fromBody) return fromBody;

  const seg = doc.location?.pathname.split('/').filter(Boolean).pop() ?? '';
  return seg.replace(/\.\w+$/, '') || 'index';
}

/** Copy <title> from fetched document */
function setDocumentTitleFrom(doc: Document): void {
  const title = doc.querySelector('title')?.textContent?.trim();
  if (title) document.title = title;
}

/** Fade helpers: honor actual CSS transition timing, fallback to TRANSITION_MS */
function fadeOut(el: HTMLElement): Promise<void> {
  return new Promise(res => {
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      el.removeEventListener('transitionend', finish);
      res();
    };
    const cs = getComputedStyle(el);
    const ms = Math.max(
      (parseFloat(cs.transitionDuration || '0') +
        parseFloat(cs.transitionDelay || '0')) *
        1000,
      TRANSITION_MS
    );
    el.addEventListener('transitionend', finish, { once: true });
    setTimeout(finish, ms + 50); // safety margin
    el.classList.add('is-fading');
  });
}

function fadeIn(el: HTMLElement): void {
  requestAnimationFrame(() => el.classList.remove('is-fading'));
}

/** Run page-specific init; keep cleanup for the next navigation */
async function runPageInit(pageId: string): Promise<void> {
  if (!pages[pageId]) return;
  try {
    const mod = await pages[pageId]();
    currentCleanup?.();
    currentCleanup = mod.cleanup;
    await mod.init?.();
  } catch (err) {
    console.warn(`[page:${pageId}] init failed:`, err);
  }
}

/** Re-run global enhancements after a swap (dropdowns, a11y focus to #content) */
function enhanceCommon(): void {
  initDropdowns();

  const content = document.querySelector<HTMLElement>(CONTENT_SELECTOR);
  if (content) {
    content.setAttribute('tabindex', '-1');
    content.focus({ preventScroll: true });
    content.removeAttribute('tabindex');
  }
}

/* Core navigation ----------------------------------------------------------------------*/
/**
 * Soft-navigate to a URL by fetching the target page and replacing #content.
 * – Updates title
 * – Pushes/replaces history
 * – Restores scroll on back/forward
 * – Honors hash targets with native CSS smooth scrolling (we avoid double smoothing)
 * Falls back to hard navigation if anything fails.
 */
async function navigateTo(
  url: string,
  opts?: { replace?: boolean; fromHistory?: boolean }
): Promise<void> {
  const contentEl = document.querySelector<HTMLElement>(CONTENT_SELECTOR);
  if (!contentEl) {
    console.warn(`${CONTENT_SELECTOR} not found; hard nav to`, url);
    window.location.href = url;
    return;
  }

  inFlight?.abort();
  const controller = new AbortController();
  inFlight = controller;

  saveScrollInHistoryState();

  try {
    await fadeOut(contentEl);

    const res = await fetch(url, {
      signal: controller.signal,
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const html = await res.text();
    const nextDoc = new DOMParser().parseFromString(html, 'text/html');

    setDocumentTitleFrom(nextDoc);

    const nextContent = nextDoc.querySelector<HTMLElement>(CONTENT_SELECTOR);
    if (!nextContent) throw new Error(`No ${CONTENT_SELECTOR} in ${url}`);

    // Swap markup
    contentEl.innerHTML = nextContent.innerHTML;

    // History update + track path+search (for hash-only popstate detection)
    const u = new URL(url, window.location.href);
    if (!opts?.fromHistory) {
      if (opts?.replace) history.replaceState({}, '', u.toString());
      else history.pushState({}, '', u.toString());
    }
    lastPathSearch = `${u.pathname}${u.search}`;

    // Handle scroll restoration / hash
    const hash = u.hash;
    if (opts?.fromHistory) {
      restoreScrollFromState(history.state);
    } else if (hash) {
      // Let CSS control smoothing; avoid double-smoothing via JS.
      const id = hash.slice(1);
      const anchor = id ? document.getElementById(id) : null;
      if (anchor) anchor.scrollIntoView({ behavior: 'auto', block: 'start' });
    } else {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'instant' as ScrollBehavior,
      });
    }

    // Enhancements + per-page init
    enhanceCommon();
    const pageId = getNextPageId(nextDoc);
    await runPageInit(pageId);

    // Broadcast a hook for any external initializers
    document.dispatchEvent(
      new CustomEvent('page:after-swap', { detail: { container: contentEl } })
    );
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') return;
    console.warn('Soft navigation failed, falling back:', err);
    window.location.href = url;
    return;
  } finally {
    fadeIn(contentEl);
    if (inFlight === controller) inFlight = null;
  }
}

/* Click interception -------------------------------------------------------------------*/
/** Normalize paths so /index.html and trailing slashes don’t confuse “same document” */
function normalizePathname(p: string): string {
  if (p.endsWith('/index.html')) p = p.slice(0, -'/index.html'.length);
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p || '/';
}

function sameDocument(a: URL, b: URL): boolean {
  if (a.origin !== b.origin) return false;
  if (normalizePathname(a.pathname) !== normalizePathname(b.pathname))
    return false;
  const aQ = new URLSearchParams(a.search).toString();
  const bQ = new URLSearchParams(b.search).toString();
  return aQ === bQ;
}

/**
 * Intercept same-origin links and run soft navigation.
 * Same-page hash links are allowed to pass through to the browser’s native scrolling.
 */
function onDocumentClick(e: MouseEvent): void {
  const anchor = (e.target as Element | null)?.closest?.(
    'a'
  ) as HTMLAnchorElement | null;
  if (!anchor) return;

  if (
    isModifiedClick(e) ||
    (anchor.target && anchor.target !== '_self') ||
    anchor.hasAttribute('download') ||
    anchor.getAttribute('rel')?.includes('external')
  ) {
    return;
  }

  const href = anchor.getAttribute('href') || '';
  let dest: URL;
  try {
    dest = new URL(href, window.location.href);
  } catch {
    return;
  }
  if (!sameOrigin(dest)) return;

  const isPureFragment = href.trim().startsWith('#');
  const current = new URL(window.location.href);
  const hasHash = !!dest.hash;

  // If we’re staying on the same document and it’s a hash change, let the browser scroll.
  if (isPureFragment || (hasHash && sameDocument(dest, current))) {
    return;
  }

  // Otherwise perform soft navigation
  e.preventDefault();
  void navigateTo(dest.toString()).catch(err =>
    console.warn('navigateTo failed:', err)
  );
}

/* Popstate (Back/Forward) --------------------------------------------------------------*/
/**
 * If back/forward only changes the hash, let the browser handle native scrolling.
 * If the document path/search changed, soft-navigate and replace the state.
 */
function onPopState(): void {
  const curr = new URL(window.location.href);
  const currPathSearch = `${curr.pathname}${curr.search}`;

  if (currPathSearch === lastPathSearch) {
    // hash-only change → native behavior
    return;
  }

  lastPathSearch = currPathSearch;
  void navigateTo(window.location.href, {
    replace: true,
    fromHistory: true,
  }).catch(err => console.warn('popstate navigation failed:', err));
}

/* Boot ---------------------------------------------------------------------------------*/
function boot(): void {
  document.addEventListener('click', onDocumentClick, { capture: true });
  window.addEventListener('popstate', onPopState);

  lastPathSearch = `${location.pathname}${location.search}`;

  enhanceCommon();

  const initialPageId =
    document.querySelector<HTMLElement>(CONTENT_SELECTOR)?.dataset.page ||
    document.body.dataset.page ||
    'index';
  void runPageInit(initialPageId);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => boot());
} else {
  boot();
}

/*---------------------------------------------------------------------------------*/
// import './styles/index.scss';
// import { initDropdowns } from 'g3d-ui';
/* ================================================== */
/* Website Navigation + Page Lifecycle Manager */
/* ================================================== */
/**
 * What it does
 * ==================================================
 * - Intercepts same-origin <a href="/contact">Contact</a> links and loads pages via fetch
 *   Same page hash links and external links are not affected.
 *
 * - Swaps the `#content` container with a blur/fade transition
 *
 * - Saves/restores scroll position on back/forward navigation
 *
 * - Runs page-specific init/cleanup hooks if provided
 *
 * - Re-initializes common enhancements (e.g. dropdowns, focus management)
 *
 * Features
 * ==================================================
 * - MPA (multi page app) for SEO
 *   Each page is a standalone HTML document so crawlers can easily access content
 *
 * - Progressive enhancements
 *   Website is still fully functional without JS
 *   The script only adds smooth transitions + shared state management.
 *   If anything breaks, falls back to normal navigation.
 *
 * - Accessibility
 *   Focus is moved to `#content` after each content swap so
 *   screen readers can announce new content.
 *
 * Safety & Robustness
 * ==================================================
 * - Fetch navigation uses AbortController: if the user clicks multiple links quickly,
 *   only the last navigation completes (prevents race conditions & ghost renders).
 *
 * - Hard reload if fetch fails: fallback to `window.location.href = url`
 *
 * - `fetch` requests always use `credentials: 'same-origin'`.
 *
 * - `DOMParser` is used to parse the target page in memory, so its scripts don’t auto-run.
 *    This avoids XSS injection from untrusted HTML payloads.
 *
 * - Page-specific `init()` functions are wrapped in try/catch.
 *
 */

// /* ==================================================
//    Debug helpers (toggle on/off)
//    ================================================== */
// const DEBUG_NAV = true;
// const T0 = typeof performance !== 'undefined' ? performance.now() : Date.now();

// function t() {
//   const now =
//     typeof performance !== 'undefined' ? performance.now() : Date.now();
//   return `${(now - T0).toFixed(1)}ms`;
// }
// function log(...args: unknown[]) {
//   if (DEBUG_NAV) console.debug('[nav]', t(), ...args);
// }
// function group(label: string) {
//   if (DEBUG_NAV) console.groupCollapsed(`[nav] ${label} @ ${t()}`);
// }
// function groupEnd() {
//   if (DEBUG_NAV) console.groupEnd();
// }

// /* Small helpers to inspect scroll env */
// function getScrollEnv() {
//   const se = document.scrollingElement || document.documentElement;
//   const html = document.documentElement;
//   const body = document.body;
//   const navH = getComputedStyle(document.documentElement)
//     .getPropertyValue('--g3d-nav-height')
//     .trim();
//   const spHtml =
//     getComputedStyle(html).scrollPaddingTop ||
//     (getComputedStyle(html) as any)['scroll-padding-top'];
//   const spBody =
//     getComputedStyle(body).scrollPaddingTop ||
//     (getComputedStyle(body) as any)['scroll-padding-top'];
//   return {
//     scrollingElement:
//       se === html ? 'html' : se === body ? 'body' : se?.tagName || 'unknown',
//     scrollTop: se?.scrollTop ?? 0,
//     scrollHeight: se?.scrollHeight ?? 0,
//     clientHeight: se?.clientHeight ?? 0,
//     '--g3d-nav-height': navH,
//     'html.scroll-padding-top': spHtml,
//     'body.scroll-padding-top': spBody,
//   };
// }
// function logAnchorMetrics(id: string) {
//   const el = document.getElementById(id);
//   if (!el) {
//     log('anchor NOT FOUND', id);
//     return;
//   }
//   const r = el.getBoundingClientRect();
//   log('anchor metrics', {
//     id,
//     rectTop: r.top,
//     rectY: r.y,
//     rectHeight: r.height,
//   });
// }

// /* ==================================================
//    Types
//    ================================================== */
// type PageModule = { init?: () => Promise<void> | void; cleanup?: () => void };
// type PageRegistry = Record<string, () => Promise<PageModule>>;

// /* Optional lazy page modules */
// const pages: PageRegistry = {
//   // contact: () => import('../contact/'),
// };

// /* ==================================================
//    Config
//    ================================================== */
// const CONTENT_SELECTOR = '#content';
// const TRANSITION_MS = 250;
// history.scrollRestoration = 'manual';

// /* ==================================================
//    State
//    ================================================== */
// let currentCleanup: (() => void) | undefined;
// let inFlight: AbortController | null = null;
// /** Tracks last path+search to detect hash-only popstate */
// let lastPathSearch = '';

// /* ==================================================
//    Utilities
//    ================================================== */
// function isModifiedClick(e: MouseEvent): boolean {
//   return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
// }
// function sameOrigin(url: URL): boolean {
//   return url.origin === window.location.origin;
// }
// function saveScrollInHistoryState(): void {
//   const base: Record<string, unknown> =
//     typeof history.state === 'object' && history.state !== null
//       ? { ...(history.state as Record<string, unknown>) }
//       : {};
//   const next = { ...base, scrollX: window.scrollX, scrollY: window.scrollY };
//   history.replaceState(next, '', window.location.href);
//   log('saved scroll in history.state', next);
// }
// function restoreScrollFromState(state: unknown): void {
//   const s =
//     typeof state === 'object' && state !== null
//       ? (state as { scrollX?: unknown; scrollY?: unknown })
//       : {};
//   const x = typeof s.scrollX === 'number' ? s.scrollX : 0;
//   const y = typeof s.scrollY === 'number' ? s.scrollY : 0;
//   log('restoring scroll from state', { x, y, env: getScrollEnv() });
//   window.scrollTo(x, y);
// }
// function getNextPageId(doc: Document): string {
//   const fromContent =
//     doc.querySelector<HTMLElement>(CONTENT_SELECTOR)?.dataset.page;
//   if (fromContent) return fromContent;
//   const fromBody = (doc.body as HTMLElement | null)?.dataset?.page;
//   if (fromBody) return fromBody;
//   const seg = doc.location?.pathname.split('/').filter(Boolean).pop() ?? '';
//   return seg.replace(/\.\w+$/, '') || 'index';
// }
// function setDocumentTitleFrom(doc: Document): void {
//   const title = doc.querySelector('title')?.textContent?.trim();
//   if (title) {
//     document.title = title;
//     log('document.title set to', title);
//   }
// }
// function fadeOut(el: HTMLElement): Promise<void> {
//   return new Promise(res => {
//     let finished = false;
//     const finish = () => {
//       if (finished) return;
//       finished = true;
//       el.removeEventListener('transitionend', finish);
//       res();
//       log('fadeOut finished');
//     };
//     const cs = getComputedStyle(el);
//     const ms = Math.max(
//       (parseFloat(cs.transitionDuration || '0') +
//         parseFloat(cs.transitionDelay || '0')) *
//         1000,
//       TRANSITION_MS
//     );
//     log('fadeOut start', { cssDurationMs: ms, class: 'is-fading' });
//     el.addEventListener('transitionend', finish, { once: true });
//     setTimeout(finish, ms + 50);
//     el.classList.add('is-fading');
//   });
// }
// function fadeIn(el: HTMLElement): void {
//   log('fadeIn');
//   requestAnimationFrame(() => el.classList.remove('is-fading'));
// }
// async function runPageInit(pageId: string): Promise<void> {
//   if (!pages[pageId]) {
//     log(`no page module for "${pageId}"`);
//     return;
//   }
//   try {
//     group(`runPageInit("${pageId}")`);
//     const mod = await pages[pageId]();
//     currentCleanup?.();
//     currentCleanup = mod.cleanup;
//     await mod.init?.();
//     groupEnd();
//   } catch (err) {
//     groupEnd();
//     console.warn(`[page:${pageId}] init failed:`, err);
//   }
// }
// function enhanceCommon(): void {
//   group('enhanceCommon');
//   try {
//     initDropdowns();
//     const content = document.querySelector<HTMLElement>(CONTENT_SELECTOR);
//     if (content) {
//       content.setAttribute('tabindex', '-1');
//       content.focus({ preventScroll: true });
//       content.removeAttribute('tabindex');
//       log('focused #content for a11y');
//     } else {
//       log('WARN: content container not found for focus');
//     }
//   } finally {
//     groupEnd();
//   }
// }

// /* ==================================================
//    Core navigation
//    ================================================== */
// async function navigateTo(
//   url: string,
//   opts?: { replace?: boolean; fromHistory?: boolean }
// ): Promise<void> {
//   group(`navigateTo(${url})`);
//   const contentEl = document.querySelector<HTMLElement>(CONTENT_SELECTOR);
//   if (!contentEl) {
//     groupEnd();
//     console.warn(`${CONTENT_SELECTOR} not found; hard nav to`, url);
//     window.location.href = url;
//     return;
//   }

//   inFlight?.abort();
//   const controller = new AbortController();
//   inFlight = controller;

//   saveScrollInHistoryState();

//   try {
//     await fadeOut(contentEl);

//     log('fetch start');
//     const res = await fetch(url, {
//       signal: controller.signal,
//       credentials: 'same-origin',
//     });
//     log('fetch done', res.status);
//     if (!res.ok) throw new Error(`HTTP ${res.status}`);

//     const html = await res.text();
//     log('parse start');
//     const nextDoc = new DOMParser().parseFromString(html, 'text/html');
//     log('parse done');

//     setDocumentTitleFrom(nextDoc);

//     const nextContent = nextDoc.querySelector<HTMLElement>(CONTENT_SELECTOR);
//     if (!nextContent) throw new Error(`No ${CONTENT_SELECTOR} in ${url}`);

//     contentEl.innerHTML = nextContent.innerHTML;
//     log('content swapped');

//     const u = new URL(url, window.location.href);
//     if (!opts?.fromHistory) {
//       if (opts?.replace) {
//         history.replaceState({}, '', u.toString());
//         log('history.replaceState');
//       } else {
//         history.pushState({}, '', u.toString());
//         log('history.pushState');
//       }
//     }
//     lastPathSearch = `${u.pathname}${u.search}`;
//     log('lastPathSearch ->', lastPathSearch);

//     const hash = u.hash;
//     if (opts?.fromHistory) {
//       restoreScrollFromState(history.state);
//     } else if (hash) {
//       const id = hash.slice(1);
//       const anchor = id ? document.getElementById(id) : null;
//       log('hash after new page', {
//         hash,
//         anchorFound: !!anchor,
//         env: getScrollEnv(),
//       });
//       if (anchor) {
//         logAnchorMetrics(id);
//         // Use behavior:auto so global CSS smooth applies exactly once.
//         anchor.scrollIntoView({ behavior: 'auto', block: 'start' });
//         setTimeout(() => {
//           log('post-swap hash scroll settled', { env: getScrollEnv() });
//           logAnchorMetrics(id);
//         }, 250);
//       }
//     } else {
//       window.scrollTo({
//         top: 0,
//         left: 0,
//         behavior: 'instant' as ScrollBehavior,
//       });
//     }

//     enhanceCommon();
//     const pageId = getNextPageId(nextDoc);
//     await runPageInit(pageId);

//     document.dispatchEvent(
//       new CustomEvent('page:after-swap', { detail: { container: contentEl } })
//     );
//   } catch (err) {
//     if ((err as Error)?.name === 'AbortError') {
//       log('navigate aborted');
//       groupEnd();
//       return;
//     }
//     console.warn('Soft navigation failed, falling back:', err);
//     groupEnd();
//     window.location.href = url;
//     return;
//   } finally {
//     fadeIn(contentEl);
//     if (inFlight === controller) inFlight = null;
//     groupEnd();
//   }
// }

// /* ==================================================
//    Click interception
//    ================================================== */
// function normalizePathname(p: string): string {
//   if (p.endsWith('/index.html')) p = p.slice(0, -'/index.html'.length);
//   if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
//   return p || '/';
// }
// function sameDocument(a: URL, b: URL): boolean {
//   if (a.origin !== b.origin) return false;
//   if (normalizePathname(a.pathname) !== normalizePathname(b.pathname))
//     return false;
//   const aQ = new URLSearchParams(a.search).toString();
//   const bQ = new URLSearchParams(b.search).toString();
//   return aQ === bQ;
// }

// function onDocumentClick(e: MouseEvent): void {
//   const anchor = (e.target as Element | null)?.closest?.(
//     'a'
//   ) as HTMLAnchorElement | null;
//   if (!anchor) return;

//   const href = anchor.getAttribute('href') || '';
//   group(`click ${href}`);
//   log('raw anchor', {
//     href,
//     target: anchor.target || '_self',
//     modified: isModifiedClick(e),
//     rel: anchor.getAttribute('rel') || '',
//   });

//   if (
//     isModifiedClick(e) ||
//     (anchor.target && anchor.target !== '_self') ||
//     anchor.hasAttribute('download') ||
//     anchor.getAttribute('rel')?.includes('external')
//   ) {
//     log('skip: modified/external/download');
//     groupEnd();
//     return;
//   }

//   let dest: URL;
//   try {
//     dest = new URL(href, window.location.href);
//   } catch {
//     log('skip: invalid URL');
//     groupEnd();
//     return;
//   }
//   if (!sameOrigin(dest)) {
//     log('skip: cross-origin');
//     groupEnd();
//     return;
//   }

//   const isPureFragment = href.trim().startsWith('#');
//   const current = new URL(window.location.href);
//   const sameDoc = sameDocument(dest, current);
//   const hasHash = !!dest.hash;

//   log('classification', {
//     isPureFragment,
//     sameDoc,
//     hasHash,
//     dest: dest.toString(),
//     env: getScrollEnv(),
//   });

//   // ✅ Let the browser handle same-page hash links (pure '#...' or same-doc with hash)
//   if (isPureFragment || (hasHash && sameDoc)) {
//     log('bypass soft-nav: native hash scroll will run');
//     // Debug after native smooth scroll settles
//     const id = dest.hash.slice(1);
//     setTimeout(() => {
//       log('post-click native hash scroll settled', { id, env: getScrollEnv() });
//       if (id) logAnchorMetrics(id);
//     }, 300);
//     groupEnd();
//     return;
//   }

//   // Soft-nav
//   e.preventDefault();
//   log('soft-nav ->', dest.toString());
//   groupEnd();
//   void navigateTo(dest.toString()).catch(err =>
//     console.warn('navigateTo failed:', err)
//   );
// }

// /* ==================================================
//    Popstate (Back/Forward)
//    ================================================== */
// function onPopState(): void {
//   group('popstate');
//   const curr = new URL(window.location.href);
//   const currPathSearch = `${curr.pathname}${curr.search}`;
//   log('currPathSearch', currPathSearch, 'lastPathSearch', lastPathSearch);

//   // If only the hash changed, let native scroll handle it and debug it.
//   if (currPathSearch === lastPathSearch) {
//     log('hash-only popstate: letting browser handle', { env: getScrollEnv() });
//     const id = curr.hash.slice(1);
//     setTimeout(() => {
//       log('post-popstate native hash scroll settled', {
//         id,
//         env: getScrollEnv(),
//       });
//       if (id) logAnchorMetrics(id);
//     }, 300);
//     groupEnd();
//     return;
//   }

//   // Otherwise, navigate to the new document state.
//   log('document popstate: soft-nav replace');
//   lastPathSearch = currPathSearch;
//   groupEnd();
//   void navigateTo(window.location.href, {
//     replace: true,
//     fromHistory: true,
//   }).catch(err => console.warn('popstate navigation failed:', err));
// }

// /* ==================================================
//    Extra: observe hash changes (belt & suspenders)
//    ================================================== */
// window.addEventListener('hashchange', () => {
//   group('hashchange');
//   const id = location.hash.slice(1);
//   log('hashchange ->', location.hash, { env: getScrollEnv() });
//   if (id) logAnchorMetrics(id);
//   setTimeout(() => {
//     log('post-hashchange settled', { env: getScrollEnv() });
//     if (id) logAnchorMetrics(id);
//   }, 300);
//   groupEnd();
// });

// /* ==================================================
//    Boot
//    ================================================== */
// function boot(): void {
//   group('boot');
//   document.addEventListener('click', onDocumentClick);
//   window.addEventListener('popstate', onPopState);

//   lastPathSearch = `${location.pathname}${location.search}`;
//   log('initial lastPathSearch ->', lastPathSearch);
//   enhanceCommon();

//   const initialPageId =
//     document.querySelector<HTMLElement>(CONTENT_SELECTOR)?.dataset.page ||
//     document.body.dataset.page ||
//     'index';
//   log('initial pageId', initialPageId);
//   groupEnd();
//   void runPageInit(initialPageId);
// }

// if (document.readyState === 'loading') {
//   document.addEventListener('DOMContentLoaded', () => boot());
// } else {
//   boot();
// }
